import React from 'react';
import { motion } from 'framer-motion';
import { socialLinks, contactInfo, navigation } from '../data/constants';

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();

    const socialIconMap: { [key: string]: JSX.Element } = {
        Instagram: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781c-.315 0-.612-.123-.833-.344-.221-.221-.344-.518-.344-.833 0-.315.123-.612.344-.833.221-.221.518-.344.833-.344s.612.123.833.344c.221.221.344.518.344.833 0 .315-.123.612-.344.833-.221.221-.518.344-.833.344z" />
            </svg>
        ),
        Youtube: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
        ),
        Facebook: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
        ),
        Twitter: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
        )
    };

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <footer className="bg-gray-900 text-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Main footer content */}
                <div className="py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {/* Brand section */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-2"
                        >
                            <div className="flex items-center space-x-3 mb-6">
                                <img
                                    src="/images/swarsadhana-logo.svg"
                                    alt="SwarSadhana Logo"
                                    className="h-12 w-12"
                                />
                                <span className="font-cinzel text-2xl font-bold text-brand-gold">
                                    SwarSadhana
                                </span>
                            </div>
                            <p className="font-poppins text-gray-300 text-lg leading-relaxed mb-6 max-w-md">
                                Harmony in Every Note and Pose. Experience the perfect blend of traditional Indian arts
                                and modern learning technology through personalized 1-on-1 live classes.
                            </p>

                            {/* Social links */}
                            <div className="flex space-x-4">
                                {socialLinks.map((social) => (
                                    <motion.a
                                        key={social.id}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-brand-gold hover:bg-gray-700 transition-all duration-300"
                                    >
                                        {socialIconMap[social.icon]}
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>

                        {/* Quick links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <h3 className="font-cinzel text-xl font-bold text-white mb-6">Quick Links</h3>
                            <ul className="space-y-3">
                                {navigation.filter(item => !item.external).map((item) => (
                                    <li key={item.id}>
                                        <button
                                            onClick={() => scrollToSection(item.href.substring(1))}
                                            className="font-poppins text-gray-300 hover:text-brand-gold transition-colors duration-200 text-left"
                                        >
                                            {item.label}
                                        </button>
                                    </li>
                                ))}
                                <li>
                                    <a
                                        href="/privacy"
                                        className="font-poppins text-gray-300 hover:text-brand-gold transition-colors duration-200"
                                    >
                                        Privacy Policy
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="/terms"
                                        className="font-poppins text-gray-300 hover:text-brand-gold transition-colors duration-200"
                                    >
                                        Terms of Service
                                    </a>
                                </li>
                            </ul>
                        </motion.div>

                        {/* Contact info */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <h3 className="font-cinzel text-xl font-bold text-white mb-6">Contact Us</h3>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <svg className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <a
                                        href={`mailto:${contactInfo.email}`}
                                        className="font-poppins text-gray-300 hover:text-brand-gold transition-colors duration-200"
                                    >
                                        {contactInfo.email}
                                    </a>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <svg className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span className="font-poppins text-gray-300">
                                        {contactInfo.phone}
                                    </span>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <svg className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-poppins text-gray-300">
                                        {contactInfo.hours}
                                    </span>
                                </div>

                                {contactInfo.address && (
                                    <div className="flex items-start space-x-3">
                                        <svg className="w-5 h-5 text-brand-gold mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="font-poppins text-gray-300">
                                            {contactInfo.address}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Newsletter signup */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="border-t border-gray-800 py-8"
                >
                    <div className="max-w-md mx-auto text-center">
                        <h3 className="font-cinzel text-xl font-bold text-white mb-4">
                            Stay Connected
                        </h3>
                        <p className="font-poppins text-gray-300 mb-6">
                            Get updates on new courses, workshops, and special offers
                        </p>
                        <div className="flex space-x-3">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-dark-red focus:border-transparent"
                            />
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-brand-gold text-gray-900 px-5 py-2 rounded-lg font-poppins font-semibold hover:bg-yellow-500 hover:shadow-lg transition-all duration-300"
                            >
                                Subscribe
                            </motion.button>
                        </div>
                    </div>
                </motion.div>

                {/* Bottom bar */}
                <div className="border-t border-gray-800 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <p className="font-poppins text-gray-400 text-center md:text-left">
                            © {currentYear} SwarSadhana. All rights reserved.
                        </p>
                        <div className="flex items-center space-x-6">
                            <a
                                href="/privacy"
                                className="font-poppins text-gray-400 hover:text-brand-gold transition-colors duration-200 text-sm"
                            >
                                Privacy
                            </a>
                            <a
                                href="/terms"
                                className="font-poppins text-gray-400 hover:text-brand-gold transition-colors duration-200 text-sm"
                            >
                                Terms
                            </a>
                            <a
                                href="/cookies"
                                className="font-poppins text-gray-400 hover:text-brand-gold transition-colors duration-200 text-sm"
                            >
                                Cookies
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer; 
